import { type IntlShape } from 'react-intl';
import {
  isRawValueFollowedByOutputValueArrayOfTwo,
  isStrictlyObject,
} from 'utils/util/util';
import { FilterOptions } from '../../Filters/sexpr';
import {
  Filterable,
  isRangeOperator,
  TConditionOperator,
} from '../../Filters/types';
import { LIKERT_SCALE_AS_ARRAY } from '../../Inputs/LikertScaleInput';
import { toYesNoValue, YES_NO_OPTIONS } from '../../Inputs/MultipleChoiceInput';
import { INPUT_TYPES } from '../../Inputs/ValidatedInputTypes';
import { decoupleValAndCalibrationInfo } from './Calibration/utils';
import {
  computeCustomQuestionAverageValue,
  getCalibratableQuestionSortValue,
} from './helpers';

// icons for the custom question columns of each response direction, also
// shown next to the filter suggestions of those columns
export const ICON_SELF_RESPONSE_QUESTION = 'fe fe-arrow-left';
export const ICON_UPWARD_RESPONSE_QUESTION = 'fe fe-arrow-up';
export const ICON_PEER_RESPONSE_QUESTION = 'fe fe-arrow-right';
export const ICON_DOWNWARD_RESPONSE_QUESTION = 'fe fe-arrow-down';

// filter value of the "Empty" suggestion offered for optional questions
export const CUSTOM_QUESTION_FILTER_EMPTY_VALUE = '__empty__';

// suffix of the resolver holding a numeric question's single comparable
// number (what the column shows: the response, or the average across
// raters), used by the range operators
export const CUSTOM_QUESTION_NUMERIC_SUFFIX = '__numeric';

const RANGE_SEXPR_OPERATORS: Partial<Record<TConditionOperator, string>> = {
  GT: '>',
  GTE: '>=',
  LT: '<',
  LTE: '<=',
};

export interface CustomQuestionFilterOption {
  id: string;
  name: string;
}

export interface CustomQuestionForFilter {
  type?: string;
  required?: boolean;
  options?: { id: string; name: string }[];
  objects?: { id?: string; name: string }[];
  scale?: { name: string; value: number }[];
}

// the parts of a column generated by generateColumnsForCustomQuestions that
// the filter relies on
export type CustomQuestionColumn = {
  field: string;
  label: string;
  question: CustomQuestionForFilter;
  filterIcon?: string;
};

export interface CustomQuestionFilterSuggestion extends Filterable {
  name: string;
  icon?: string;
  description: string;
}

export interface CustomQuestionFilterConfig {
  suggestions: CustomQuestionFilterSuggestion[];
  // question fields (mapped to their description) holding numeric values,
  // which additionally support the range operators
  numericKeys: Record<string, string>;
  filterOptions: FilterOptions;
}

export const isCustomQuestionColumn = (column: {
  [key: string]: unknown;
}): column is CustomQuestionColumn =>
  typeof column?.field === 'string' &&
  typeof column?.label === 'string' &&
  isStrictlyObject(column?.question);

// the predefined values a response to the question can take, or undefined
// when the question is answered freely (e.g. text) and so is not filterable
export const getCustomQuestionFilterOptions = (
  question: CustomQuestionForFilter | undefined,
  formatMessage: IntlShape['formatMessage']
): CustomQuestionFilterOption[] | undefined => {
  switch (question?.type?.toUpperCase()) {
    case INPUT_TYPES.DROPDOWN:
      return question?.objects?.map((o) => ({
        id: String(o.id),
        name: o.name,
      }));
    case INPUT_TYPES.MULTIPLE_CHOICE:
    case INPUT_TYPES.SELECT:
      return question?.options?.map((o) => ({
        id: String(o.id),
        name: o.name,
      }));
    case INPUT_TYPES.YES_NO:
    case INPUT_TYPES.SWITCH:
    case INPUT_TYPES.TOGGLE:
    case INPUT_TYPES.CHECKBOX:
      return YES_NO_OPTIONS(formatMessage);
    case INPUT_TYPES.LIKERT:
      // value first, like the rating scale ("4 - Agree"), so that typing
      // the number matches the suggestion (suggestions match from the start
      // of a word)
      return (
        question?.scale?.length
          ? question.scale
          : LIKERT_SCALE_AS_ARRAY(formatMessage)
      ).map((s) => ({ id: String(s.value), name: `${s.value} - ${s.name}` }));
    default:
      return undefined;
  }
};

const normalizeScalar = (value: unknown): string[] => {
  if (value === null || value === undefined) {
    return [];
  }
  if (typeof value === 'boolean') {
    return [toYesNoValue(value) as string];
  }
  const text = String(value).trim();
  return text === '' ? [] : [text];
};

const flattenResponse = (value: unknown): string[] => {
  if (value === null || value === undefined) {
    return [];
  }
  // [raw value, display value] pairs (e.g. dropdown/choice responses)
  if (isRawValueFollowedByOutputValueArrayOfTwo(value)) {
    return normalizeScalar((value as unknown[])[0]);
  }
  if (Array.isArray(value)) {
    return value.flatMap((item) =>
      isStrictlyObject(item)
        ? normalizeScalar(item.full_name ?? item.name)
        : flattenResponse(item)
    );
  }
  // one response per rater (e.g. peer or upward feedback)
  if (isStrictlyObject(value)) {
    return Object.values(value as object).flatMap(flattenResponse);
  }
  return normalizeScalar(value);
};

// the comparable values held by a row's response to a custom question:
// option ids for choice questions, "true"/"false" for yes/no questions;
// one entry per rater for multi-rater questions; empty when there is no
// response
export const getCustomQuestionFilterValues = (rawValue: unknown): string[] => {
  const { val } = decoupleValAndCalibrationInfo(rawValue);
  return flattenResponse(val);
};

// whether responses to the question are numbers, so that the column can be
// filtered by range
export const isNumericCustomQuestion = (
  question: CustomQuestionForFilter | undefined
): boolean => question?.type?.toUpperCase() === INPUT_TYPES.LIKERT;

// the single number a numeric question's response compares as, matching
// what the column shows: the (calibrated) response, or the average across
// raters for multi-rater questions; undefined when there is no response
export const getCustomQuestionNumericValue = (
  rawValue: unknown
): number | undefined => {
  const { val, calibrationInfo } = decoupleValAndCalibrationInfo(rawValue);
  const value = isStrictlyObject(val)
    ? computeCustomQuestionAverageValue(val, calibrationInfo)
    : getCalibratableQuestionSortValue(rawValue);
  const number = Number(value);
  return value === null ||
    value === undefined ||
    value === '' ||
    Number.isNaN(number)
    ? undefined
    : number;
};

export const getCustomQuestionFieldTransformer =
  (fieldName: string) =>
  (value: Filterable, operator: TConditionOperator): string => {
    const isEq = operator === 'EQ';
    const filterValue = value.filterObject.value;
    if (filterValue === CUSTOM_QUESTION_FILTER_EMPTY_VALUE) {
      return isEq ? `(is-empty ${fieldName})` : `(not (is-empty ${fieldName}))`;
    }
    if (isRangeOperator(operator)) {
      return `(${RANGE_SEXPR_OPERATORS[operator]} ${fieldName}${CUSTOM_QUESTION_NUMERIC_SUFFIX} "${filterValue}")`;
    }
    return `(${isEq ? 'in' : 'not-in'} "${filterValue}" ${fieldName})`;
  };

export const buildCustomQuestionFilterConfig = (
  columns: { [key: string]: unknown }[],
  formatMessage: IntlShape['formatMessage'],
  baseFilterOptions: FilterOptions = {}
): CustomQuestionFilterConfig => {
  const suggestions: CustomQuestionFilterSuggestion[] = [];
  const numericKeys: Record<string, string> = {};
  const fieldTransformers = { ...baseFilterOptions.fieldTransformers };
  const fieldResolvers = { ...baseFilterOptions.fieldResolvers };

  for (const column of columns) {
    if (!isCustomQuestionColumn(column)) {
      continue;
    }
    const { field, label: keyDescription, question, filterIcon } = column;
    const options = getCustomQuestionFilterOptions(question, formatMessage);
    if (!options) {
      continue;
    }
    const toSuggestion = (
      value: string,
      name: string
    ): CustomQuestionFilterSuggestion => ({
      name,
      icon: filterIcon,
      description: keyDescription,
      filterObject: { key: field, value, keyDescription },
    });

    options.forEach((o) => suggestions.push(toSuggestion(o.id, o.name)));
    if (!question.required) {
      suggestions.push(
        toSuggestion(
          CUSTOM_QUESTION_FILTER_EMPTY_VALUE,
          formatMessage({
            id: 'app.views.widgets.dashboards.team_dashboard.filters.empty',
            defaultMessage: 'Empty',
          })
        )
      );
    }

    fieldTransformers[field] = getCustomQuestionFieldTransformer(field);
    fieldResolvers[field] = (row) =>
      getCustomQuestionFilterValues(row?.[field]);
    if (isNumericCustomQuestion(question)) {
      numericKeys[field] = keyDescription;
      fieldResolvers[field + CUSTOM_QUESTION_NUMERIC_SUFFIX] = (row) =>
        getCustomQuestionNumericValue(row?.[field]);
    }
  }

  return {
    suggestions,
    numericKeys,
    filterOptions: { fieldTransformers, fieldResolvers },
  };
};
